import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, ShoppingCart, Plus, Minus, Star, Clock, MapPin } from 'lucide-react';
import { backendGet } from '../lib/backendClient';
import { resolveStoreLogo, resolveStoreBanner } from '../lib/storeImages';
import { normalizeExtraLabel } from '../lib/extras';
import { getDisplayedBasePriceReais, getPromotionDiscountPercent, getStandardProductPriceReais, hasActivePromotion } from '../lib/pricing';
import { getExtraPriceCents, getExtraPriceReais, getSelectedExtrasUnitTotalReais } from '../lib/extraPricing';
import { useCart } from '../contexts/CartContext';
import { useAuth } from '../contexts/AuthContext';
import './RestaurantMenu.css';

const ADDITIONALS_LABEL = 'Adicionais';
const RESTAURANT_MENU_CACHE_TTL_MS = 5000;
const restaurantMenuCache = new Map();

export default function RestaurantMenu() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { addItem, items, restaurantId, increaseQuantity, decreaseQuantity, syncProductCustomization, subtotal, itemCount } = useCart();

  const [restaurant, setRestaurant] = useState(null);
  const [products, setProducts] = useState([]);
  const [selectedExtrasByProduct, setSelectedExtrasByProduct] = useState({});
  const [expandedByProduct, setExpandedByProduct] = useState({});
  const [removalNotesByProduct, setRemovalNotesByProduct] = useState({});
  const [loading, setLoading] = useState(true);
  const isClosed = restaurant?.is_open === false;
  const requestSeqRef = useRef(0);

  const isSameRestaurantCart = restaurantId && restaurant && restaurantId === restaurant.id;

  const getQuantityForSelection = (productId, selectedExtras = [], removalNote = '') => {
    if (!isSameRestaurantCart) return 0;
    const cartItemKey = buildCartItemKey(productId, selectedExtras, removalNote);
    const existing = items.find((item) => (item.cart_item_key || String(item.id)) === cartItemKey);
    return Number(existing?.quantity || 0);
  };

  const getProductExtras = (product) => {

    const rawExtras = Array.isArray(product?.extras)
      ? product.extras
      : (Array.isArray(product?.adicionais)
        ? product.adicionais
        : (Array.isArray(product?.sitio) ? product.sitio : []));

    return rawExtras
      .filter((extra) => extra && typeof extra === 'object' && extra.enabled !== false && (extra.name || extra.nome))
      .map((extra, index) => ({
        id: String(extra.id || `${product?.id}-extra-${index}`),
        name: normalizeExtraLabel(extra.name || extra.nome),
        max_quantity: Number(extra.max_quantity || extra.maximo || 1),
        price_cents: getExtraPriceCents(extra),
      }))
      .filter((extra) => extra.name);
  };

  const getSelectedExtrasForProduct = (productId) => {
    return Array.isArray(selectedExtrasByProduct[productId]) ? selectedExtrasByProduct[productId] : [];
  };

  const normalizeRemovalNote = (value) => String(value || '').trim().replace(/\s+/g, ' ');

  const buildCartItemKey = (productId, selectedExtras, removalNote = '') => {
    const extrasKey = (Array.isArray(selectedExtras) ? selectedExtras : [])
      .map((extra) => `${extra.id}:${extra.quantity || 1}`)
      .sort()
      .join('|');
    const removeKey = normalizeRemovalNote(removalNote).toLowerCase();
    if (!extrasKey && !removeKey) return String(productId);

    const parts = [String(productId)];
    if (extrasKey) parts.push(`extras=${extrasKey}`);
    if (removeKey) parts.push(`remove=${removeKey}`);
    return parts.join('::');
  };

  const getRemovalNoteForProduct = (productId) => normalizeRemovalNote(removalNotesByProduct[productId] || '');

  const setProductExpanded = (productId, expanded) => {
    setExpandedByProduct((prev) => ({ ...prev, [productId]: expanded }));
  };

  const toggleProductExpanded = (productId) => {
    setExpandedByProduct((prev) => ({ ...prev, [productId]: !prev[productId] }));
  };

  const updateRemovalNote = (productId, value) => {
    setRemovalNotesByProduct((prev) => ({ ...prev, [productId]: value }));
  };

  const toggleExtraSelection = (product, extra, checked) => {
    if (!product) return;

    const productId = product.id;
    const current = getSelectedExtrasForProduct(productId);
    const sourceCartItemKey = buildCartItemKey(productId, current, getRemovalNoteForProduct(productId));
    let nextSelected;

    if (checked) {
      if (current.some((item) => item.id === extra.id)) return;
      const priceCents = getExtraPriceCents(extra);
      nextSelected = [
        ...current,
        {
          id: extra.id,
          name: extra.name,
          quantity: 1,
          price_cents: priceCents,
          subtotal_cents: priceCents,
        },
      ];
    } else {
      nextSelected = current.filter((item) => item.id !== extra.id);
    }

    setSelectedExtrasByProduct((prev) => ({
      ...prev,
      [productId]: nextSelected,
    }));

    syncProductCustomization(product, nextSelected, getRemovalNoteForProduct(productId), sourceCartItemKey);
  };

  const handleIncrease = (product) => {
    if (isClosed) return;

    if (!user) {
      navigate('/login');
      return;
    }

    if (!restaurant) return;

    const selectedExtras = getSelectedExtrasForProduct(product.id);
    const removalNote = getRemovalNoteForProduct(product.id);
    const cartItemKey = buildCartItemKey(product.id, selectedExtras, removalNote);

    setProductExpanded(product.id, true);

    if (isSameRestaurantCart) {
      const existing = items.find((item) => item.cart_item_key === cartItemKey);
      if (existing) {
        increaseQuantity(existing.cart_item_key || existing.id);
        return;
      }
    }

    const preco = getDisplayedBasePriceReais(product);
    const precoOriginal = getStandardProductPriceReais(product);
    const productWithQuantity = {
      ...product,
      preco,
      preco_original: precoOriginal,
      quantity: 1,
      selected_extras: selectedExtras,
      removal_note: removalNote,
      removed_ingredients: removalNote,
      cart_item_key: cartItemKey,
    };

    addItem(productWithQuantity, {
      id: restaurant.id,
      name: restaurant.nome || restaurant.name || 'Restaurante',
    });
  };

  const handleDecrease = (product) => {
    if (isClosed) return;
    if (!isSameRestaurantCart) return;
    const selectedExtras = getSelectedExtrasForProduct(product.id);
    const removalNote = getRemovalNoteForProduct(product.id);
    const cartItemKey = buildCartItemKey(product.id, selectedExtras, removalNote);
    const existing = items.find((item) => item.cart_item_key === cartItemKey);
    if (existing) {
      decreaseQuantity(existing.cart_item_key || existing.id);
    }
  };

  useEffect(() => {
    let isMounted = true;
    const currentRequestSeq = ++requestSeqRef.current;

    async function loadData() {
      try {
        const cacheKey = String(id || '');
        const cached = restaurantMenuCache.get(cacheKey);
        const now = Date.now();
        if (cached && (now - cached.ts) <= RESTAURANT_MENU_CACHE_TTL_MS) {
          if (!isMounted || currentRequestSeq !== requestSeqRef.current) return;
          setRestaurant(cached.restaurant || null);
          setProducts(
            Array.isArray(cached.products)
              ? cached.products.filter((product) => product?.available !== false)
              : []
          );
          setLoading(false);
          return;
        }

        const [restaurantData, productsData] = await Promise.all([
          backendGet(`/api/restaurants/${id}`, { skipAuth: true }),
          backendGet(`/api/products/${id}`, { skipAuth: true }),
        ]);
        if (!isMounted || currentRequestSeq !== requestSeqRef.current) return;

        const normalizedProducts = Array.isArray(productsData)
          ? productsData.filter((product) => product?.available !== false)
          : [];
        restaurantMenuCache.set(cacheKey, {
          ts: Date.now(),
          restaurant: restaurantData || null,
          products: normalizedProducts,
        });
        setRestaurant(restaurantData || null);
        setProducts(normalizedProducts);

      } catch (err) {
        if (err?.name === 'AbortError') return;
        console.error('Erro ao carregar dados:', err);
      } finally {
        if (isMounted && currentRequestSeq === requestSeqRef.current) {
          setLoading(false);
        }
      }
    }

    loadData();

    return () => {
      isMounted = false;
    };
  }, [id]);

  const handleAddToCart = (product) => {
    if (isClosed) return;

    if (!user) {
      navigate('/login');
      return;
    }

    if (!restaurant) return;

    const quantityToAdd = 1;
    const preco = getDisplayedBasePriceReais(product);
    const precoOriginal = getStandardProductPriceReais(product);
    const selectedExtras = getSelectedExtrasForProduct(product.id);
    const removalNote = getRemovalNoteForProduct(product.id);
    const cartItemKey = buildCartItemKey(product.id, selectedExtras, removalNote);

    setProductExpanded(product.id, true);

    const productWithQuantity = {
      ...product,
      preco,
      preco_original: precoOriginal,
      quantity: quantityToAdd,
      selected_extras: selectedExtras,
      removal_note: removalNote,
      removed_ingredients: removalNote,
      cart_item_key: cartItemKey,
    };

    addItem(productWithQuantity, {
      id: restaurant.id,
      name: restaurant.nome || restaurant.name || 'Restaurante',
    });

  };


  if (loading) {
    return (
      <div className="restaurant-menu-loading">
        Carregando cardapio...
      </div>
    );
  }

  if (!restaurant) {
    return (
      <div className="restaurant-menu-not-found">
        <p className="restaurant-menu-not-found-text">Restaurante nao encontrado</p>
        <button
          className="restaurant-menu-not-found-btn"
          onClick={() => navigate('/')}
        >
          Voltar para inicio
        </button>
      </div>
    );
  }

  const restaurantLogoImage = resolveStoreLogo(restaurant);
  const menuBackgroundImage = resolveStoreBanner(restaurant);

  return (
    <div className="restaurant-menu-page">
      <header className="restaurant-menu-header">
        <div className="restaurant-menu-header-inner">
          <button
            className="restaurant-menu-back-btn"
            onClick={() => navigate('/')}
          >
            <ArrowLeft size={20} />
            Voltar
          </button>
        </div>
      </header>

      {isClosed && <div className="restaurant-menu-closed-bar">Loja fechada no momento. Nao e possivel fazer pedidos.</div>}

      <div className="restaurant-menu-store-header">
        <div className="restaurant-menu-store-header-inner">
          <div className="restaurant-menu-store-row">
            <img
              className="restaurant-menu-store-logo"
              src={restaurantLogoImage}
              alt={restaurant.nome || restaurant.name}
            />
            <div className="restaurant-menu-store-meta">
              <h1
                className="restaurant-menu-store-name"
                onClick={() => navigate(`/store/${id}/info`)}
                title="Ver informacoes da loja"
              >
                {restaurant.nome || restaurant.name || 'Restaurante'}
              </h1>
              <p className="restaurant-menu-store-category">
                {restaurant['areas operacionais'] || restaurant.category || 'Restaurante'}
              </p>
              <div className="restaurant-menu-store-stats">
                <div className="restaurant-menu-store-stat">
                  <Star size={16} fill="#FFB800" stroke="#FFB800" />
                  <span>4.5</span>
                </div>
                <div className="restaurant-menu-store-stat">
                  <Clock size={16} />
                  <span>{restaurant.delivery_time || '30-40 min'}</span>
                </div>
                <div className="restaurant-menu-store-stat">
                  <MapPin size={16} />
                  <span>Entrega: R$ {(Number(restaurant.delivery_fee || 0)).toFixed(2).replace('.', ',')}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="restaurant-menu-content">
        <div
          className="restaurant-menu-panel"
          style={{
            background: menuBackgroundImage ? `url(${menuBackgroundImage}) center / cover no-repeat` : 'linear-gradient(135deg, #fff4e8 0%, #ffe9d1 100%)',
          }}
        >
          <div
            className="restaurant-menu-panel-overlay"
            style={{ background: menuBackgroundImage ? 'linear-gradient(180deg, rgba(18,18,18,0.30) 0%, rgba(18,18,18,0.55) 100%)' : 'transparent' }}
          />

          <div className="restaurant-menu-panel-content">
            <h2 className="restaurant-menu-section-title">
              Cardapio
            </h2>
            <p className="restaurant-menu-section-subtitle">
              {products.length > 0
                ? `${products.length} ${products.length === 1 ? 'item disponivel' : 'itens disponiveis'} para pedido`
                : 'Confira as opcoes desta loja'}
            </p>

            {products.length === 0 ? (
              <div className="restaurant-menu-empty">
                Nenhum produto disponivel no momento.
              </div>
            ) : (
              <div className="restaurant-menu-products">
                {products.map((product) => {
                  const productName = product.nome || product.name;
                  const productDescription = product.descricao || product.description || 'Sem descricao';
                  const productBasePrice = getStandardProductPriceReais(product) || 0;
                  const productEffectivePrice = getDisplayedBasePriceReais(product) || 0;
                  const promotionActive = hasActivePromotion(product) && productEffectivePrice < productBasePrice;
                  const productDiscountPercent = getPromotionDiscountPercent(product);
                  const productExtras = getProductExtras(product);
                  const selectedExtras = getSelectedExtrasForProduct(product.id);
                  const hasExtrasOrRemoval = true;
                  const isExpanded = Boolean(expandedByProduct[product.id]);
                  const showCustomizationPanel = hasExtrasOrRemoval && isExpanded;
                  const removalNote = getRemovalNoteForProduct(product.id);
                  const quantity = getQuantityForSelection(product.id, selectedExtras, removalNote);
                  const selectedExtrasUnitValue = getSelectedExtrasUnitTotalReais(selectedExtras);
                  const productFinalUnitPrice = productEffectivePrice + selectedExtrasUnitValue;

                  return (
                    <div
                      key={product.id}
                      className={`restaurant-menu-product-card${showCustomizationPanel ? ' is-expanded' : ''}`}
                    >
                      <div className="restaurant-menu-product-main">
                        <h3 className="restaurant-menu-product-name">{productName}</h3>
                        <p className="restaurant-menu-product-description">{productDescription}</p>
                        <div className="restaurant-menu-product-price">
                          {promotionActive && (
                            <span className="restaurant-menu-product-price-old">
                              R$ {productBasePrice.toFixed(2).replace('.', ',')}
                            </span>
                          )}
                          <span className="restaurant-menu-product-price-current">
                            R$ {productFinalUnitPrice.toFixed(2).replace('.', ',')}
                          </span>
                          {promotionActive && productDiscountPercent > 0 && (
                            <span className="restaurant-menu-product-discount">
                              {Math.round(productDiscountPercent)}% OFF
                            </span>
                          )}
                        </div>

                        {hasExtrasOrRemoval && (
                          <button
                            type="button"
                            className="restaurant-menu-customize-btn"
                            onClick={() => toggleProductExpanded(product.id)}
                          >
                            {showCustomizationPanel ? 'Ocultar personalização' : 'Personalizar pedido'}
                          </button>
                        )}

                        {showCustomizationPanel && (
                          <div className="restaurant-menu-customization-panel">
                            {productExtras.length > 0 && (
                              <div className="restaurant-menu-custom-group">
                                <p className="restaurant-menu-custom-group-title">{ADDITIONALS_LABEL}</p>
                                <div className="restaurant-menu-extra-list">
                                  {productExtras.map((extra) => {
                                    const checked = selectedExtras.some((item) => item.id === extra.id);
                                    return (
                                      <label
                                        key={extra.id}
                                        className="restaurant-menu-extra-item"
                                      >
                                        <input
                                          type="checkbox"
                                          checked={checked}
                                          onChange={(event) => toggleExtraSelection(product, extra, event.target.checked)}
                                        />
                                        <span>{extra.name} (+R$ {getExtraPriceReais(extra).toFixed(2).replace('.', ',')})</span>
                                      </label>
                                    );
                                  })}
                                </div>
                              </div>
                            )}

                            <div className="restaurant-menu-custom-group">
                              <p className="restaurant-menu-custom-group-title">Retirar ingredientes (opcional)</p>
                              <input
                                type="text"
                                value={removalNote}
                                maxLength={120}
                                onChange={(event) => updateRemovalNote(product.id, event.target.value)}
                                placeholder="Ex.: sem cebola, sem tomate"
                                className="restaurant-menu-remove-input"
                              />
                            </div>
                          </div>
                        )}

                        <div className="restaurant-menu-product-controls">
                          <div className="restaurant-menu-qty-cluster">
                            <button
                              type="button"
                              className="restaurant-menu-qty-btn"
                              onClick={() => handleDecrease(product)}
                              disabled={isClosed}
                            >
                              <Minus size={16} />
                            </button>

                            <span className="restaurant-menu-qty-value">{quantity}</span>

                            <button
                              type="button"
                              className="restaurant-menu-qty-btn"
                              onClick={() => handleIncrease(product)}
                              disabled={isClosed}
                            >
                              <Plus size={16} />
                            </button>
                          </div>

                          <button
                            type="button"
                            className="restaurant-menu-add-btn"
                            onClick={() => handleAddToCart(product)}
                            disabled={isClosed}
                          >
                            Adicionar a sacola
                          </button>
                        </div>
                      </div>

                      {(product.imagem || product.image_url) && (
                        <img
                          className="restaurant-menu-product-image"
                          src={product.imagem || product.image_url}
                          alt={productName}
                        />
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>

      {itemCount > 0 && !isClosed && (
        <div className="restaurant-menu-cart-fab-wrap">
          <button
            className="restaurant-menu-cart-fab"
            onClick={() => navigate('/checkout')}
          >
            <ShoppingCart size={20} />
            <span>{itemCount} {itemCount === 1 ? 'item' : 'itens'}</span>
            <span>R$ {subtotal.toFixed(2).replace('.', ',')}</span>
            <span className="restaurant-menu-cart-fab-label">Finalizar pedido</span>
          </button>
        </div>
      )}
    </div>
  );
}

